/**
 * CustomerCombobox — pick an existing customer or create a new one.
 *
 * Two explicit modes instead of the old "type a name and maybe we'll offer to
 * create it" dropdown row:
 *
 *   search — type any part of a name, email or phone (the API matches every
 *            word you type, so "sarah smith" finds Sarah Smith). Results list
 *            below; picking one selects it.
 *   create — a "New customer" button (always visible, and offered inline when
 *            a search finds nobody) opens a short form: name, contact details
 *            and the address the transport step later relies on.
 *
 * The selected customer collapses to a summary row with a "Change" action.
 */
import { What3WordsInput } from '@/components/What3WordsInput';
import { createCustomer, searchCustomers } from '@/lib/api';
import type { CreateCustomerInput, Customer } from '@/lib/types';
import { cn } from '@/lib/utils';
import { isWhat3Words } from '@/lib/what3words';
import { Loader2, Plus, Search, UserPlus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface Props {
  value: Customer | null;
  onChange: (c: Customer | null) => void;
  /** Focus the search box on mount (the wizard's first field). */
  autoFocus?: boolean;
}

interface NewCustomerForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addressLine1: string;
  city: string;
  postcode: string;
  what3words: string;
}

const emptyForm = (): NewCustomerForm => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  addressLine1: '',
  city: '',
  postcode: '',
  what3words: '',
});

/** "Sarah Smith" → { firstName: 'Sarah', lastName: 'Smith' }; emails/phones → blank. */
function splitName(q: string): { firstName: string; lastName: string } {
  const text = q.trim();
  if (!text || text.includes('@') || /^[\d\s+()-]+$/.test(text)) {
    return { firstName: '', lastName: '' };
  }
  const [first, ...rest] = text.split(/\s+/);
  return { firstName: first ?? '', lastName: rest.join(' ') };
}

const inputCls =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30';

export function CustomerCombobox({ value, onChange, autoFocus = false }: Props) {
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<Customer[]>([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<NewCustomerForm>(emptyForm());
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search — fires 200ms after the last keystroke, ≥ 2 chars.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const text = q.trim();
    if (text.length < 2) {
      setMatches([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounce.current = setTimeout(() => {
      searchCustomers(text)
        .then((rows) => {
          setMatches(rows);
          setSearched(true);
        })
        .catch(() => {
          setMatches([]);
          setSearched(true);
        })
        .finally(() => setLoading(false));
    }, 200);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [q]);

  const startCreate = () => {
    setForm({ ...emptyForm(), ...splitName(q) });
    setCreateError('');
    setMode('create');
  };

  const cancelCreate = () => {
    setMode('search');
    setForm(emptyForm());
    setCreateError('');
  };

  const submitCreate = async () => {
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    if (!firstName || !lastName) {
      setCreateError('First and last name are required.');
      return;
    }
    const email = form.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setCreateError('That email address does not look right.');
      return;
    }
    const what3words = form.what3words.trim();
    if (what3words && !isWhat3Words(what3words)) {
      setCreateError('Enter a valid what3words address (e.g. filled.count.soap).');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const body: CreateCustomerInput = {
        firstName,
        lastName,
        email: email || null,
        phone: form.phone.trim() || null,
        addressLine1: form.addressLine1.trim() || null,
        city: form.city.trim() || null,
        postcode: form.postcode.trim() || null,
        what3words: what3words || null,
      };
      const created = await createCustomer(body);
      onChange(created);
      setMode('search');
      setForm(emptyForm());
      setQ('');
      setMatches([]);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create customer');
    } finally {
      setCreating(false);
    }
  };

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-border bg-surface px-3 py-2">
        <div>
          <p className="text-sm font-medium">
            {value.firstName} {value.lastName}
          </p>
          <p className="text-xs text-muted-foreground">
            {[value.email, value.phone].filter(Boolean).join(' · ') || 'No contact details'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-muted-foreground hover:text-foreground press"
        >
          Change
        </button>
      </div>
    );
  }

  if (mode === 'create') {
    const set = (k: keyof NewCustomerForm, v: string) => setForm((f) => ({ ...f, [k]: v }));
    return (
      <form
        className="space-y-3 rounded-lg border border-border bg-surface p-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submitCreate();
        }}
        aria-label="New customer"
      >
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-sm font-medium">
            <UserPlus className="size-4 text-primary" strokeWidth={1.75} />
            New customer
          </p>
          <button
            type="button"
            onClick={cancelCreate}
            className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary press"
            aria-label="Cancel new customer"
          >
            <X className="size-3.5" strokeWidth={1.75} />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className={labelCls}>First name</span>
            <input
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              className={inputCls}
              autoFocus
              autoComplete="off"
            />
          </label>
          <label className="block">
            <span className={labelCls}>Last name</span>
            <input
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              className={inputCls}
              autoComplete="off"
            />
          </label>
          <label className="block">
            <span className={labelCls}>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              className={inputCls}
              autoComplete="off"
            />
          </label>
          <label className="block">
            <span className={labelCls}>Phone</span>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              className={inputCls}
              autoComplete="off"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className={labelCls}>Address line 1</span>
            <input
              value={form.addressLine1}
              onChange={(e) => set('addressLine1', e.target.value)}
              className={inputCls}
              autoComplete="off"
              placeholder="Needed for pickup / dropoff pricing"
            />
          </label>
          <label className="block">
            <span className={labelCls}>Town / city</span>
            <input
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              className={inputCls}
              autoComplete="off"
            />
          </label>
          <label className="block">
            <span className={labelCls}>Postcode</span>
            <input
              value={form.postcode}
              onChange={(e) => set('postcode', e.target.value)}
              className={inputCls}
              autoComplete="off"
            />
          </label>
          <div className="sm:col-span-2">
            <span className={labelCls}>what3words (optional)</span>
            <What3WordsInput
              value={form.what3words}
              onChange={(v) => set('what3words', v)}
              aria-label="what3words"
            />
          </div>
        </div>
        {createError && <p className="text-xs text-destructive">{createError}</p>}
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={creating}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground press disabled:opacity-50"
          >
            {creating && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
            Save customer
          </button>
          <button
            type="button"
            onClick={cancelCreate}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground press"
          >
            Back to search
          </button>
        </div>
      </form>
    );
  }

  const text = q.trim();
  const showResults = text.length >= 2;

  return (
    <div>
      <div className="flex items-stretch gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-border bg-surface px-3">
          <Search className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email or phone"
            aria-label="Search customers"
            className="w-full bg-transparent py-2 text-sm outline-none"
            autoFocus={autoFocus}
            autoComplete="off"
          />
          {loading && (
            <Loader2
              className="size-4 shrink-0 animate-spin text-muted-foreground"
              strokeWidth={1.75}
            />
          )}
        </div>
        <button
          type="button"
          onClick={startCreate}
          className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-primary hover:bg-primary/5 press"
        >
          <Plus className="size-4" strokeWidth={2} />
          New customer
        </button>
      </div>

      {showResults && (
        <div className="mt-2 overflow-hidden rounded-lg border border-border bg-surface">
          {matches.slice(0, 20).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onChange(c);
                setQ('');
                setMatches([]);
              }}
              className="flex w-full items-baseline justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-secondary press"
            >
              <span className="font-medium">
                {c.firstName} {c.lastName}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {[c.email, c.phone].filter(Boolean).join(' · ')}
              </span>
            </button>
          ))}
          {searched && !loading && matches.length === 0 && (
            <div className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                No customers match &ldquo;{text}&rdquo;.
              </p>
              <button
                type="button"
                onClick={startCreate}
                className={cn(
                  'flex items-center gap-1.5 self-start rounded-md bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary press',
                )}
              >
                <Plus className="size-3.5" strokeWidth={2} />
                Add &ldquo;{text}&rdquo; as a new customer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const labelCls =
  'mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground';
